import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { useVisualizationColors } from '../../hooks/useVisualizationColors';
import fillColors from './fillColors.module.scss';
import fillColorsLightened from './fillColorsLightened.module.scss';
import strokeColors from './strokeColors.module.scss';
import css from './index.module.scss';

export const CHART_POINT_HIGHLIGHT_SCALE_UP = 2.25;

export interface ChartPointProps {
  x: number;
  y: number;
  r: number;
  /**
   * The color of the circle. When highlighted, the outer ring's color is lightened 20%.
   */
  color?: keyof typeof fillColors;
  /**
   * Hollows the circle instead of it having a fill.
   *
   * Example use case: partial time periods.
   */
  hollow?: boolean;
  /**
   * Applies a faded outer ring around the primary circle, same fill color.
   */
  highlight?: boolean;
}

export const ChartPoint: React.FC<ChartPointProps> = ({
  x,
  y,
  r,
  color: colorProp,
  hollow = false,
  highlight = false,
}) => {
  const translate = `translate(${x}px, ${y}px)`;
  const outerScale = highlight ? CHART_POINT_HIGHLIGHT_SCALE_UP : 1;
  const innerScale = highlight ? CHART_POINT_HIGHLIGHT_SCALE_UP * (2 / 3) : 1;

  const VISUALIZATION_COLORS = useVisualizationColors();
  const color = colorProp ?? VISUALIZATION_COLORS[0];

  let innerCircleStrokeWidth = undefined;
  if (hollow) {
    // strokeWidth is a function of the size of the point
    const startingStrokeWidth = r / 2;
    // keep strokeWidth constant, scale inversely down
    innerCircleStrokeWidth = startingStrokeWidth / innerScale;
  }

  return (
    <g data-component="ChartPoint" data-testid="chart-point">
      {/* the outer circle hides behind the below "inner" circle except in highlight mode
      when it transitions to a larger outer size representing the background "ring" */}
      <circle
        r={r}
        className={classNames(css.chartDot, fillColorsLightened[color])}
        style={{ transform: `${translate} scale(${outerScale})` }}
      />
      {/* inner circle, sits on top of outer */}
      <circle
        r={r}
        className={classNames(css.chartDot, {
          [fillColors[color]]: !hollow,
          [css.fillDefault]: hollow,
          [strokeColors[color]]: hollow,
        })}
        strokeWidth={innerCircleStrokeWidth}
        style={{ transform: `${translate} scale(${innerScale})` }}
      />
    </g>
  );
};
